<template>
  <div id="ClinicianSelection">
    <div v-if="loading" class="d-flex flex-column align-items-center justify-content-center">
      <b-spinner class="brand-primary"></b-spinner>
    </div>

    <div class="gp-select-container w-100 mb-5 pb-5 px-4" ref="gpSelectTop" v-else>
      <div
        class="d-flex flex-column align-items-center content-container booking-container-width w-100 animate__animated animate__fadeIn"
        v-if="selectedView === 'dateClinicianSelect'">
        <div class="clinician-selection">
          <div v-if="noDates" class="no-appointments">
            <div v-if="isDmhFlow" class="dmh-call-now">
              <div class="dmh-call-now-icon">
                <i class="fas fa-phone-alt" aria-hidden="true"></i>
              </div>
              <span class="dmh-call-now-status">Counsellors available now</span>
              <h3 class="dmh-call-now-title">You can speak with a counsellor right now</h3>
              <p class="dmh-call-now-intro">
                There are no online appointments available to book at the moment. You don't need to wait for one.
                Our counsellors are available by phone, and you can start talking today.
              </p>
              <div class="dmh-call-now-card">
                <ul class="dmh-call-now-benefits">
                  <li v-for="benefit in callNowBenefits" :key="benefit.title">
                    <span class="dmh-call-now-tick">
                      <i class="fas fa-check" aria-hidden="true"></i>
                    </span>
                    <div>
                      <strong>{{ benefit.title }}</strong>
                      <p>{{ benefit.detail }}</p>
                    </div>
                  </li>
                </ul>
                <b-button :href="primaryCallContact.href" class="btn-continue dmh-call-now-button" size="lg">
                  <i class="fas fa-phone-alt" aria-hidden="true"></i>
                  <span>Call now</span>
                  <span class="dmh-call-now-number">· {{ primaryCallContact.value }}</span>
                </b-button>
                <ul v-if="additionalContacts.length" class="dmh-call-now-additional">
                  <li v-for="(contact, index) in additionalContacts" :key="index">
                    {{ contact.label }}
                    <a v-if="contact.href" :href="contact.href">{{ contact.value }}</a>
                    <span v-else>{{ contact.value }}</span>
                  </li>
                </ul>
              </div>
            </div>
            <p v-else class="no-appointments-text">
              No appointments are currently available. Please check back later.
            </p>
          </div>
          <div v-else-if="dates">
            <h3 class="dmh-start-title">
              Thank you! Let's book you in with a clinician
            </h3>
          </div>
          <div
            class="content-container d-flex flex-column justify-content-center align-items-center animate__animated animate__fadeIn">
            <div class="date-selector-container" v-if="dates">
              <h4 class="dmh-section-title">Select a date</h4>
              <div class="slick-container">
                <slick ref="slickRef" class="slick-class" :options="slickOptions">
                  <div v-for="(value, key) in dates" :key="key"
                    class="d-flex align-items-center justify-content-center">
                    <b-button :class="{ active: key === selectedDate }" class="btn-date-time fixed-width"
                      @click="selectDate(key)">
                      {{ getFormatedDate(key, "DD MMM YYYY") }}
                    </b-button>
                  </div>
                </slick>
              </div>
            </div>
            <div v-if="selectedDate" class="clinicians-selector-container">
              <h4 class="dmh-section-title w-100">
                Clinicians available
              </h4>
              <div class="dmh-clinicians-container">
                <button class="btn-dmh-clinician" :class="{ active: i === selectedClinicianIndex }"
                  v-for="(clinician, i) in dates[selectedDate]" :key="i"
                  @click="selectClinicianIndex(i, clinician.clinicianId)">
                  <div class="dmh-clinician-name">
                    <span>{{ clinician.name }}</span>
                    <p class="clinician-info mt-1">{{ clinician.skills }}</p>
                  </div>
                  <span v-if="clinician.recommended && !isDmhFlow" class="recommended-label">Recommended!</span>
                </button>
                <button class="btn-dmh-clinician" :class="{ active: 'No Preference' === selectedClinicianIndex }"
                  @click="selectClinicianIndex('No Preference')" v-if="showNoPreferenceOption && !isDmhFlow">
                  <div class="dmh-clinician-name">
                    <span>No Preference</span>
                    <p class="clinician-info mt-1">See times for all available clinicians</p>
                  </div>
                </button>
              </div>
            </div>
            <div
              v-if="noDates && isDmhFlow"
              class="d-flex justify-content-center align-self-end pt-5 dmh-btn-container"
            >
              <b-button class="btn-continue" size="lg" @click="returnToHome">Return to home</b-button>
            </div>
            <div
              v-else
              class="d-flex justify-content-center align-self-end pt-5 dmh-btn-container"
            >
              <b-button size="lg" class="btn-cancel py-4 px-5" @click="previous(questionHistory)">Previous</b-button>
              <b-button class="btn-continue" size="lg" :disabled="!selectedClinicianId"
                @click="selectView('timeslotSelect')"><span v-if="!appointmentLoading">Continue</span><b-spinner
                  v-else></b-spinner></b-button>
            </div>
          </div>
        </div>
      </div>
      <div
        class="d-flex flex-column align-items-center content-container booking-container-width w-100 animate__animated animate__fadeIn"
        v-if="selectedView === 'timeslotSelect'">
        <div class="clinician-selection">
          <div v-if="dates">
            <h3 class="dmh-start-title">
              On {{ getFormatedDate(selectedDate, "DD MMM YYYY") }}, you're
              seeing
            </h3>
          </div>
          <div
            class="content-container d-flex flex-column justify-content-center align-items-center animate__animated animate__fadeIn">
            <div class="dmh-clinicians-container">
              <div class="dmh-clinician active">
                <div class="dmh-clinician-name" v-if="selectedClinicianIndex !== 'No Preference'">
                  <span>{{
                    dates[selectedDate][selectedClinicianIndex].name
                    }}</span>
                  <p class="clinician-info mt-1">
                    {{ dates[selectedDate][selectedClinicianIndex].skills }}
                  </p>
                </div>
                <div class="dmh-clinician-name" v-else>
                  <span>No Preference</span>
                </div>
              </div>
              <button class="change-btn" @click="selectView('dateClinicianSelect')">
                Change date or clinician
              </button>
            </div>
            <div class="w-100">
              <h4 class="select-time-title">{{ selectTimeTitle }}</h4>
              <div class="dmh-timeslots-container" v-if="selectedClinicianIndex !== 'No Preference'">
                <div class="dmh-timeslot-item" v-for="(timeslot, i) in dates[selectedDate][
                  selectedClinicianIndex
                ].timeslots" :key="i">
                  <b-button :class="{ active: timeslot === selectedTimeslot }" class="btn-date-time"
                    @click="selectTimeslot(timeslot)">
                    {{ formatSlotForDisplay(timeslot) }}
                  </b-button>
                </div>
              </div>
              <div class="dmh-timeslots-container" v-else>
                <div class="dmh-timeslot-item" v-for="(timeslot, i) in collatedDates[selectedDate].timeslots" :key="i">
                  <b-button :class="{ active: timeslot === selectedTimeslot }" class="btn-date-time"
                    @click="selectTimeslot(timeslot)">
                    {{ formatSlotForDisplay(timeslot) }}
                  </b-button>
                </div>
              </div>
            </div>
            <div class="tcs-container">
              <p class="mb-0 text-center">
                <button @click="toggleTcAcceptence()" class="tc-button">
                  <i class="clickable tc-checkbox mr-1" :class="[
                    !tcsAccepted
                      ? 'fa-regular fa-square grey'
                      : 'fa fa-check-square filled',
                  ]"></i>
                </button>
                I have read and agree to the terms and conditions of this
                service listed
                <a :href="termsAndConditionsLink" target="_blank" class="tcs-link pl-1">here</a>.
              </p>
            </div>
            <div
              class="d-flex justify-content-center align-self-end pt-5 dmh-btn-container"
            >
              <b-button
                class="btn-cancel py-4 px-4"
                size="lg"
                :disabled="appointmentLoading"
                squared
                @click="selectView('dateClinicianSelect')"
                >Previous</b-button
              >
              <b-button
                class="btn-continue"
                size="lg"
                squared
                :disabled="
                  !selectedTimeslot || !tcsAccepted || appointmentLoading
                "
                @click="confirmBooking"
                ><span v-if="!appointmentLoading">{{
                  isServiceIncludedInPlan
                    ? "Confirm booking"
                    : "Confirm &amp; pay"
                }}</span><b-spinner v-else></b-spinner></b-button>
            </div>
          </div>
        </div>
      </div>
      <BookingConfirmed
        v-if="selectedView === 'bookingConfirm'"
        :serviceType="serviceType"
        :onNavigate="onNavigate"
        :returnButtonText="returnButtonText"
        :onReturn="onReturn"
      />
    </div>

    <div v-if="errorModal" class="error-modal-overlay" @click.self="closeErrorModal">
      <div class="error-modal-dialog">
        <ErrorModal
          :title="errorModal.title"
          :error="errorModal.error"
          :redirect="errorModal.redirect"
          :goBack="errorModal.goBack"
          :bupaTouchLink="bupaTouchLink"
          :onNavigate="onNavigate"
          @close="closeErrorModal"
        />
      </div>
    </div>
  </div>
</template>

<script>
import slick from "vue-slick";
import moment from "moment";
import ErrorModal from "./ErrorModal.vue";
import BookingConfirmed from "./BookingConfirmed.vue";
import {
  getFormatedDate,
  scrollToTop,
  formatSlotForDisplay,
  formatAppointmentTimeForApi,
  formatSlotAsIso8601,
  rebucketSlotsByLocalDate,
  getViewerTimeZoneLabel,
  isAustralianApiUrl,
  buildEapContactHref,
} from "../../utils/helpers";
import journeyMixin from "../../mixins/journeyMixin";

const DEFAULT_SUPPORT_CONTACT = ["UK", "0345 608 0898"];

const toEapContact = (label, value) => ({ label, value, href: buildEapContactHref(label, value) });

export default {
  name: "ClinicianSelection",
  mixins: [journeyMixin],
  props: {
    reset: Function,
    previous: Function,
    questionHistory: Array,
    genderPreference: String,
    bookingJourneyId: String,
    userId: { type: String, required: true },
    serviceType: { type: String, default: "" },
    apiQuestionnaire: { type: Object, required: true },
    onNavigate: Function,
    serviceData: { type: Object, default: () => ({}) },
    bupaTouchLink: { type: String, default: "" },
    locale: { type: String, default: "" },
    returnButtonText: { type: String, default: "Return to Home" },
    onReturn: { type: Function, default: null },
    apiUrl: { type: String, default: "" },
    // The member's stored timezone preference (TD-192). Empty falls back to the
    // device clock, which is only right for a host that has no preference to give.
    userTimezone: { type: String, default: "" },
    termsUrl: { type: String, default: "" },
    vpcTermsUrl: { type: String, default: "" },
    eapPhoneNumbers: { type: Array, default: () => [] },
  },
  components: {
    slick,
    BookingConfirmed,
    ErrorModal,
  },
  data() {
    return {
      loading: true,
      selectedView: "dateClinicianSelect",
      selectedDate: null,
      selectedFilter: "morning",
      selectedClinicianIndex: null,
      selectedClinicianId: null,
      selectedTimeslot: null,
      dates: false,
      // Kept so the buckets can be rebuilt if the preference arrives after the slots.
      rawDates: null,
      slotTimeZone: null,
      coallatedDates: false,
      clinicians: [],
      timeslots: [],
      timeslotsLoading: true,
      appointmentLoading: false,
      noDates: false,
      userDetails: { symptoms: "", photos: [] },
      tcsAccepted: false,
      showNoPreferenceOption: false,
      errorModal: null,
      slickOptions: {
        infinite: false,
        nextArrow: `<button type="button" class="slick-next"><i class="fa fa-caret-right" aria-hidden="true"></i></button>`,
        prevArrow: `<button type="button" class="slick-prev"><i class="fa fa-caret-left" aria-hidden="true"></i></button>`,
        slidesToScroll: 4,
        slidesToShow: 4,
        responsive: [
          {
            breakpoint: 700,
            settings: {
              slidesToShow: 3,
              slidesToScroll: 3,
            },
          },
          {
            breakpoint: 520,
            settings: {
              slidesToShow: 2,
              slidesToScroll: 2,
            },
          },
        ],
      },
    };
  },
  mounted() {
    if (!this.serviceType) {
      this.onNavigate?.("/");
    }
    this.scrollToTop();
    this.getFlags();
    this.getDates();
  },
  methods: {
    scrollToTop() {
      this.$nextTick(() => scrollToTop(this.$el));
    },
    getFormatedDate(date, format) {
      return getFormatedDate(date, format, this.locale);
    },
    applyDateBuckets() {
      this.dates = rebucketSlotsByLocalDate(this.rawDates, this.displayTimeZone);
      // Re-bucketing can move a slot to the next or previous day, so a date the
      // member had already picked may no longer be a key — the template indexes
      // straight into dates[selectedDate], so drop a selection that went stale.
      if (this.selectedDate && !(this.selectedDate in (this.dates || {}))) {
        this.selectDate(null);
      }
    },
    formatSlotForDisplay(timeslot) {
      return formatSlotForDisplay(timeslot, this.displayTimeZone);
    },
    // start_date/start_time remain the slot list's own wall-clock for backwards
    // compatibility; start_date_time_iso_8601 carries the same moment with its
    // offset attached, so the backend never has to infer which zone was meant.
    appointmentTimePayload() {
      const apiTime = formatAppointmentTimeForApi(
        this.selectedTimeslot,
        this.slotTimeZone,
      );
      const startDate = apiTime.startDate || this.selectedDate;
      const isoInstant = formatSlotAsIso8601(
        this.selectedTimeslot,
        startDate,
        this.slotTimeZone,
      );
      const payload = { start_date: startDate, start_time: apiTime.startTime };
      // Omitted rather than sent null when it can't be resolved: the backend
      // falls back to the legacy pair on an absent field, not a null one.
      if (isoInstant) payload.start_date_time_iso_8601 = isoInstant;
      return payload;
    },
    async confirmBooking() {
      if (this.isServiceIncludedInPlan) {
        await this.createAppointment();
      } else {
        await this.createAppointmentStripe();
      }
    },
    selectDate(date) {
      this.selectedDate = date;
      this.selectedClinicianIndex = null;
      this.selectedTimeslot = null;
      this.selectedClinicianId = null;
      // this.getTimeslots();
    },
    selectView(view) {
      if (view === "dateClinicianSelect") {
        this.selectedDate = null;
        this.selectedClinicianId = null;
      }
      this.selectedView = view;
    },
    toggleTcAcceptence() {
      this.tcsAccepted = !this.tcsAccepted;
    },
    selectTimeslot(time) {
      this.selectedTimeslot = time;
    },
    selectClinicianIndex(i, id) {
      if (id) {
        this.selectedClinicianId = id;
        this.selectedTimeslot = null;
        this.selectedClinicianIndex = i;
      } else{
        this.selectedClinicianId = 'No Preference';
        this.selectedTimeslot = null;
        this.selectedClinicianIndex = 'No Preference';
      }
    },
    // DMH's empty state promises counsellors are available now, which a failed lookup can't
    // back up, so a failure gets the error pop-up instead (CR-405).
    handleDatesLoadFailure() {
      if (this.isDmhFlow) {
        this.openErrorModal("Sorry, something went wrong", "Please try again later");
      } else {
        this.noDates = true;
      }
    },
    returnToHome() {
      if (this.onReturn) this.onReturn();
      else this.onNavigate?.("/");
    },
    buttonDisabled(date) {
      let today = moment().format("YYYY-MM-DD");
      return moment(date).isBefore(today);
    },
    async getFlags() {
      try {
        const res = await this.apiQuestionnaire.getFeatureFlags({
          feature_flags: ["vpc_no_preference_clinician_web"]
        });
        if (res.data?.success) {
          this.showNoPreferenceOption = res.data.feature_flags.vpc_no_preference_clinician_web;
        }
      } catch (err) {
        console.error(err);
      }
    },
    async getDates() {
      try {
        const payload = {
          user_id: this.userId,
          gender_preference: this.genderPreference,
          service_type: this.serviceType,
          booking_journey_id: this.bookingJourneyId
        };
        const res = await this.apiQuestionnaire.getClinicianDates(payload);
        if (res.data.success) {
          // The zone these slots are quoted in (e.g. "Australia/Sydney") — the
          // booking payload has to name it rather than assume the viewer's.
          this.slotTimeZone = res.data.timezone || null;
          this.rawDates = res.data.dates;
          this.applyDateBuckets();
          if (!this.dates || Object.keys(this.dates).length === 0) {
            this.noDates = true;
            if (this.isDmhFlow) this.dates = false;
          }
        } else {
          this.handleDatesLoadFailure();
        }
      } catch(err) {
        console.log(err)
        this.handleDatesLoadFailure();
      } finally {
        this.loading = false;
      }
    },
    getTitleString(tags) {
      let title = "";
      tags.forEach((tag) => {
        title = title + tag + ", ";
      });
      title = title.substring(0, title.length - 2);
      return title;
    },
    async createAppointment() {
      if (this.appointmentLoading) {
        return;
      }
      this.appointmentLoading = true;
      try {
        const payload = {
          ...this.appointmentTimePayload(),
          user_id: this.userId,
          clinician_id: this.selectedClinicianId,
          service_type: this.serviceType,
          booking_journey_id: this.bookingJourneyId,
        };
        const res = await this.apiQuestionnaire.bookAppointment(payload);
        if (res.data.success) {
          if (
            res.data.apiStatus ===
            "Appointment has been already booked for the same slot. Please choose different clinician or slot."
          ) {
            this.openErrorModal(
              "Sorry, this appointment is no longer available",
              "Please choose another appointment"
            );
          } else if (res.data.apiStatus?.toLowerCase() === "success") {
            this.selectView("bookingConfirm");
            this.$emit("hideProgressBar");
          } else {
            this.openErrorModal(
              "Sorry, something went wrong",
              "Please try again later"
            );
          }
        } else {
          this.openErrorModal(
            "Sorry, something went wrong",
            "Please try again later"
          );
        }
      } catch (err) {
        console.error(err);
        this.openErrorModal(
          "Sorry, something went wrong",
          "Please try again later"
        );
      } finally {
        this.appointmentLoading = false;
      }
    },
    async createAppointmentStripe() {
      this.appointmentLoading = true;
      try {
        const payload = {
          ...this.appointmentTimePayload(),
          user_id: this.userId,
          clinician_id: this.selectedClinicianId,
          service_type: this.serviceType,
          booking_journey_id: this.bookingJourneyId,
        };
        const res = await this.apiQuestionnaire.getPaymentLink(payload);
        if (res.data.success) {
          window.location.replace(res.data.link);
        } else {
          this.openErrorModal(
            "Sorry, something went wrong",
            "Please try again"
          );
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.appointmentLoading = false;
      }
    },
    openErrorModal(title, error, redirect, goBack) {
      this.errorModal = { title, error, redirect: !!redirect, goBack: !!goBack };
    },
    closeErrorModal() {
      this.errorModal = null;
    },
  },
  watch: {
    // The host may resolve the preference after the slots land; rebuild rather
    // than leave the strip bucketed by a zone the times are no longer shown in.
    displayTimeZone() {
      if (this.rawDates) this.applyDateBuckets();
    },
    // Each step of the selection (dates -> timeslot -> confirm) should start at the
    // top, not wherever the member had scrolled to on the previous one.
    selectedView() {
      this.scrollToTop();
    },
    dates(dates){
      let collatedDates = {};
      for(let day in dates){
        let allTimeslots = [];
        dates[day].forEach((d) => {
          allTimeslots = allTimeslots.concat(d.timeslots);
        })
        let filteredTimeslots = [...new Set(allTimeslots)]
        filteredTimeslots.sort((a,b) => {
              return (a < b) ? -1 : ((a > b) ? 1 : 0);
        })
        collatedDates[day] = {timeslots: filteredTimeslots};
      }
      this.collatedDates = collatedDates;
    }
  },
  computed: {
    isDmhFlow() {
      return this.serviceType && this.serviceType.includes("dmh")
    },
    eapContacts() {
      return this.eapPhoneNumbers.map(([label, value]) => toEapContact(label, value));
    },
    // First callable EAP number is the CTA; WhatsApp/SMS can't back "Call now", so a
    // client with no callable number gets the default support line instead.
    primaryCallContact() {
      return (
        this.eapContacts.find((contact) => contact.href?.startsWith("tel:")) ??
        toEapContact(...DEFAULT_SUPPORT_CONTACT)
      );
    },
    additionalContacts() {
      return this.eapContacts.filter((contact) => contact !== this.primaryCallContact);
    },
    callNowBenefits() {
      return [
        { title: "Support straight away", detail: "No waiting for an appointment slot to open up." },
        { title: "The same qualified team", detail: "You'll speak with an accredited mental health counsellor." },
        { title: "Free and confidential, 24/7", detail: "Call any time, day or night." },
      ];
    },
    termsAndConditionsLink() {
      if (this.vpcTermsUrl) {
        return this.vpcTermsUrl;
      }
      if (this.termsUrl) {
        return this.termsUrl;
      }
      if (this.serviceType && this.serviceType.includes("dmh")) {
        return "/terms-and-conditions/wh_tcs";
      } else {
        return "/terms-and-conditions-spark";
      }
    },
    isServiceIncludedInPlan() {
      // Host opts into free booking by passing serviceData.included_in_your_plan = true.
      // When false/undefined (the default), confirmBooking routes through Stripe via
      // createAppointmentStripe and getPaymentLink.
      return !!this.serviceData?.included_in_your_plan;
    },
    // "" means "no preference" — helpers then fall back to the device clock.
    displayTimeZone() {
      return this.userTimezone || "";
    },
    selectTimeTitle() {
      // TD-192 is Australia-only: every other host keeps the unqualified title,
      // since there is no picker there for the member to change the zone with.
      if (!isAustralianApiUrl(this.apiUrl)) return "Select a time";
      const timeZoneLabel = getViewerTimeZoneLabel(
        this.displayTimeZone || undefined,
      );
      return timeZoneLabel
        ? `Select a time (${timeZoneLabel})`
        : "Select a time";
    },
  },
};
</script>
<style lang="less">
#ClinicianSelection {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 650px;
  margin: 0 auto;

  .error-modal-overlay {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    padding: 16px;
  }

  .error-modal-dialog {
    background-color: #fff;
    border-radius: 16px;
    max-width: 500px;
    width: 100%;
    max-height: calc(100vh - 32px);
    overflow-y: auto;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  }

  .btn-cancel {
    border: 1px solid var(--brand-primary);
    padding: 16px;
    color: var(--brand-primary);
    font-family: var(--font-bold, "DMSans Bold");
    background-color: #fff;
    letter-spacing: normal;
    border-radius: 15px !important;
  }

  .btn-continue {
    padding: 16px;
    color: #fff;
    font-family: var(--font-bold, "DMSans Bold");
    background-color: var(--brand-primary);
    letter-spacing: normal;
    border-radius: 15px !important;
  }

  .clinicians-selector-container {
    width: 100%;
  }

  .tcs-link {
    font-family: var(--font-bold, "DMSans Bold");
    text-decoration: underline;
    color: var(--brand-primary);
  }

  .tc-button {
    border: none;
    background-color: unset;
    padding: 0;
    accent-color: var(--brand-primary);
  }

  .tc-checkbox {
    font-size: 2rem;
    padding-left: 5px;

    &.grey {
      color: #aeb0bd;
    }

    &.filled {
      color: #1e1e22;
    }
  }

  .dmh-btn-container {
    gap: 24px;
  }

  .recommended-label {
    padding: 8px 16px;
    background-color: rgba(183, 182, 190, 0.3);
    border-radius: 40px;
    font-size: 1.2rem;
    font-family: var(--font-bold, "DMSans Bold");
    color: #1e1e22;
  }

  .dmh-clinicians-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;

    .dmh-clinician {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      text-align: left;
      width: 100%;
      padding: 20px;
      border-radius: 24px;
      margin-bottom: 20px;
      background-color: #fff;
      box-shadow: 0px 4px 24px 0px #00000012;

      .active {
        background-color: #fff;
      }
    }

    .dmh-clinician-name {
      color: #1e1e22;
      font-family: var(--font-bold, "DMSans Bold");
      text-align: left;

      .clinician-info {
        font-size: 1.4rem;
        line-height: 2.2rem;
        color: #797b89;
        margin-bottom: 0;
      }
    }

    .dmh-clinician-avatar {
      border-radius: 100px;

      img {
        height: 55px;
        margin-right: 25px;
        border-radius: 1000px;
        overflow: hidden;
      }
    }
  }

  .fixed-width {
    max-width: 150px;
  }

  .sentence-case::first-letter {
    text-transform: uppercase;
  }

  .shadow {
    box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.2);
  }

  .booking-confirm-container {
    font-size: 1.8rem;
  }

  .booking-button {
    min-width: 200px !important;
  }

  .change-btn {
    color: var(--brand-primary);
    text-decoration: underline;
    font-family: var(--font-bold, "DMSans Bold");
    font-size: 1.4rem;
    background: unset;
    border: unset;
    align-self: flex-start;
    padding: 0;
    margin-bottom: 32px;
  }

  .tc-checkbox.filled {
    color: var(--brand-primary);
  }

  .slick-container {
    width: 100%;
    max-width: 616px;
  }

  .slick-slider {
    position: relative;
  }

  .slick-next,
  .slick-prev {
    position: absolute;
    z-index: 10;
    top: 50%;
    background-color: transparent;
    border: none;
    padding: 0 6px;
    transform: translateY(-50%);

    .fa {
      font-size: 3rem;
      color: #1e1e22;
    }
  }

  .slick-next {
    right: -30px;
  }

  .slick-prev {
    left: -30px;
  }

  .slick-slide {
    transform: none;
    margin: 0 6px;
  }

  .slick-disabled {
    display: none !important;
  }

  .VueCarousel-navigation-button {
    font-size: 15px;
    top: 18px;
  }

  .container-width {
    max-width: 450px;
  }

  .booking-container-width {
    max-width: 800px;
  }

  .btn {
    min-width: 100px;
    margin-bottom: 10px;
  }

  .timeslot-button {
    color: #21272a;
  }

  .selected-date {
    color: white;

    &:hover {
      color: white;
    }
  }

  .preview-image {
    border-radius: 1000px;
    border: 6px solid;
    border-color: inherit;
    margin-bottom: 25px;
  }

  .no-appointments {
    font-size: 1.6rem;
  }

  .no-appointments-text {
    padding-top: 25px;
    font-size: 1.6rem;
  }

  .dmh-start-title {
    color: #1e1e22;
    text-align: left;
    font-size: 2.2rem;
    letter-spacing: 0;
    padding: 0;
    font-family: var(--font-bold, "DMSans Bold");
    margin-bottom: 40px;
  }

  .dmh-section-title {
    text-align: left;
    font-size: 1.8rem;
    letter-spacing: 0;
    font-family: var(--font-bold, "DMSans Bold");
    color: #1e1e22;
    padding: 0;
    margin-bottom: 20px;
  }

  .select-time-title {
    color: #1e1e22;
    text-align: left;
    font-size: 2.2rem;
    letter-spacing: 0;
    font-family: var(--font-bold, "DMSans Bold");
    margin-bottom: 16px;
  }

  .tc-link {
    color: #0079c8;
    text-decoration: underline;
  }

  .no-appointments {
    font-size: 1.6rem;
  }

  .no-appointments-text {
    padding-top: 25px;
    font-size: 1.6rem;
  }

  .dmh-call-now {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 560px;
    margin: 0 auto;
    padding-top: 25px;
    color: #1e1e22;
  }

  .dmh-call-now-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    font-size: 2.4rem;
    color: var(--brand-primary);
    background-color: color-mix(in srgb, var(--brand-primary) 14%, #fff);
  }

  .dmh-call-now-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 16px;
    padding: 4px 12px;
    border-radius: 40px;
    font-size: 1.2rem;
    font-family: var(--font-bold, "DMSans Bold");
    color: #1e1e22;
    background-color: rgba(40, 167, 69, 0.12);

    &::before {
      content: "";
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: var(--success, #28a745);
    }
  }

  .dmh-call-now-title {
    margin: 16px 0 8px;
    padding: 0;
    text-align: center;
    letter-spacing: 0;
    font-size: 2.2rem;
    font-family: var(--font-bold, "DMSans Bold");
    color: #1e1e22;
  }

  .dmh-call-now-intro {
    font-size: 1.6rem;
    line-height: 1.5;
    margin-bottom: 24px;
  }

  .dmh-call-now-card {
    width: 100%;
    padding: 20px;
    border: 1px solid #aeb0bd;
    border-radius: 16px;
    background-color: #fff;
    text-align: left;
  }

  .dmh-call-now-benefits {
    list-style: none;
    margin: 0 0 20px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;

    li {
      display: flex;
      gap: 12px;
      align-items: flex-start;
    }

    strong {
      font-size: 1.6rem;
      font-family: var(--font-bold, "DMSans Bold");
    }

    p {
      margin: 2px 0 0;
      font-size: 1.4rem;
      color: #797b89;
    }
  }

  .dmh-call-now-tick {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    margin-top: 1px;
    border-radius: 50%;
    font-size: 1rem;
    color: var(--brand-primary);
    background-color: color-mix(in srgb, var(--brand-primary) 14%, #fff);
  }

  .dmh-call-now-button {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
  }

  .dmh-call-now-number {
    font-family: var(--font-regular, "DMSans");
  }

  .dmh-call-now-additional {
    list-style: none;
    margin: 16px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    text-align: center;
    font-size: 1.4rem;

    a {
      color: var(--brand-primary);
      text-decoration: underline;
    }
  }

  .fa-4x {
    color: green;
  }

  .tcs-container {
    align-self: flex-start;

    p {
      color: #1e1e22;
    }
  }

  .btn-dmh-clinician {
    width: 100%;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    align-items: center;
    border: 2px solid #fff;
    background-color: #fff;
    box-shadow: 0px 4px 24px 0px #00000012;
    padding: 20px;
    border-radius: 24px;
    flex-wrap: wrap;
    background-color: #fff;
    color: #1e1e22;

    &:hover,
    &.active {
      border: 2px solid var(--brand-primary);
    }
  }

  .review-details {
    width: 100%;

    .text-prompt-container {
      max-width: 700px;
      font-size: 1.6rem;

      h3 {
        padding-top: 0;
        margin-top: 0;
        letter-spacing: normal;
        font-size: 1.8rem;
      }

      .details-container {
        width: 100%;
        background-color: white;
        border: 1px solid #dde1e6;

        .details-row {
          border-bottom: 1px solid #dde1e6;
          padding: 20px;
          word-wrap: break-word;
          text-align: left;

          .details-row-title {
            font-weight: 500;
            margin-bottom: 0;
            padding-bottom: 0;
          }

          .edit-button {
            align-self: start;
            cursor: pointer;
            font-weight: bold;
          }
        }
      }
    }

    .preview-image {
      max-width: 150px;
      min-width: 150px;
      min-height: 150px;
      max-height: 150px;
      object-fit: scale-down;
      border-radius: 16px;
      background-color: #eee;
    }
  }

  b {
    font-family: var(--font-bold, "DMSans Bold");
  }

  .modal-content.coach_modal {
    border-radius: 16px;
  }

  .clinician-selection {
    width: 100%;
    max-width: 100%;
    font-size: 1.6rem;

    .button-group {
      border: 1px solid #0079c8;
      border-radius: 5px;
      background-color: white;
    }

    .date-selector-container {
      max-width: 650px;
      border-radius: 12px;
      padding: 20px 0;
      width: 100%;
      margin-bottom: 12px;
    }

    .dmh-timeslots-container {
      display: grid;
      gap: 16px 12px;
      grid-template-columns: auto auto auto;
      margin-bottom: 32px;
    }

    .btn-date-time {
      border: 2px solid #aeb0bd;
      background-color: #fff;
      color: #1e1e22;
      padding: 12px 18px;
      font-family: var(--font-bold, "DMSans Bold");
      font-size: 1.6rem;
      letter-spacing: normal;
      border-radius: 16px;
      width: 100%;
      margin-bottom: 0;

      &:hover,
      &.active {
        border: 2px solid var(--brand-primary);
        background-color: rgba(126, 108, 216, 0.05);
      }
    }

    .date-select-button {
      background-color: white;
      cursor: pointer;
      padding: 10px;
      font-size: 1.8rem !important;
      border: 1px solid white;
      color: #0079c8;
      border-radius: 8px;

      &:hover {
        background-color: #eee;
        border: 1px solid #eee;
      }

      .month-class {
        font-size: 1.4rem;
        color: #ccc;
      }
    }

    .preview-image {
      height: 80px;
      border-radius: 1000px;
      border: 3px solid;
      border-color: inherit;
    }

    .date-select-button:disabled,
    .date-select-button[disabled] {
      cursor: not-allowed;
      background-color: white;
      padding: 10px;
      font-size: 1.8rem !important;
      border: 1px solid white;
      border-radius: 8px;
      color: #ccc;

      &:hover {
        border: 1px solid white;
      }
    }

    .timeslot-button {
      color: #21272a;
      border: 1px solid #0079c8;
    }

    .selected-date {
      background-color: #0079c8;
      color: white;

      &:hover {
        background-color: #0079c8;
        color: white !important;
      }
    }

    .no-appointments {
      font-size: 1.6rem;
    }

    .no-appointments-text {
      padding-top: 25px;
      font-size: 1.6rem;
    }
  }

  @media only screen and (max-width: 380px) {
    .clinician-selection .dmh-timeslots-container {
      grid-template-columns: auto auto;
    }
  }

  @media only screen and (max-width: 576px) {
    .clinician-selection .dmh-clinicians-container .dmh-clinician {
      gap: 16px;
      flex-direction: column;
      align-items: flex-start;
    }

    .dmh-btn-container {
      gap: 16px;
    }

    .btn-dmh-clinician {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  @media only screen and (max-width: 700px) {
    .slick-next {
      right: -20px;
    }

    .slick-prev {
      left: -20px;
    }
  }
}
</style>
